/** IAA 简单统计契约：业务 UTC 日与平台报表日独立，不生成跨口径衍生指标。 */
import { z } from 'zod';

export const rewardedAdMetricsDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(
    (value) =>
      !value.startsWith('0000-') &&
      Number.isFinite(Date.parse(`${value}T00:00:00Z`)) &&
      new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value,
    '必须为合法 YYYY-MM-DD 日期',
  );
export const RewardedAdMetricsQuerySchema = z
  .object({
    app_scope: z
      .union([z.literal('all'), z.uuidv7()])
      .default('all')
      .describe('all 或原生 App UUID v7'),
    from: rewardedAdMetricsDateSchema.describe('起始日期（包含）'),
    to: rewardedAdMetricsDateSchema.describe('结束日期（包含，不晚于昨天 UTC）'),
  })
  .strict();
export type RewardedAdMetricsQuery = z.infer<typeof RewardedAdMetricsQuerySchema>;
export function validateRewardedAdMetricsWindow(input: RewardedAdMetricsQuery, now: Date): boolean {
  // 服务也可直接调用窗口校验；不能让 JS 接受的零年或归一化日期进入 PostgreSQL。
  if (
    !rewardedAdMetricsDateSchema.safeParse(input.from).success ||
    !rewardedAdMetricsDateSchema.safeParse(input.to).success
  )
    return false;
  const start = Date.parse(`${input.from}T00:00:00Z`);
  const end = Date.parse(`${input.to}T00:00:00Z`);
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return start <= end && (end - start) / 86_400_000 < 90 && end < today;
}
const count = z.string().regex(/^\d+$/);
const pending = z.object({ status: z.literal('PENDING_INSTRUMENTATION') }).strict();
export const RewardedAdBusinessMetricsSchema = z.union([
  pending,
  z
    .object({
      status: z.literal('READY'),
      valid_ssv_count: count,
      bonus_granted_amount: count,
      ad_unlock_count: count,
    })
    .strict(),
]);
export const RewardedAdRevenueMetricsSchema = z.union([
  pending,
  z
    .object({
      status: z.literal('READY'),
      estimates: z.array(
        z
          .object({
            currency: z.string(),
            report_timezone: z.string(),
            amount_minor_units: z.string().regex(/^-?\d+$/),
            is_estimated: z.literal(true),
            updated_at: z.iso.datetime(),
          })
          .strict(),
      ),
    })
    .strict(),
]);
export const RewardedAdMetricsResponseSchema = z
  .object({
    from: rewardedAdMetricsDateSchema,
    to: rewardedAdMetricsDateSchema,
    business_timezone: z.literal('UTC'),
    rows: z.array(
      z
        .object({
          date: rewardedAdMetricsDateSchema,
          app_id: z.uuidv7(),
          placement: z.enum(['TASK_CENTER', 'CHAPTER_UNLOCK']),
          business: RewardedAdBusinessMetricsSchema,
          revenue: RewardedAdRevenueMetricsSchema,
        })
        .strict(),
    ),
  })
  .strict();
export type RewardedAdMetricsResponse = z.infer<typeof RewardedAdMetricsResponseSchema>;
export type RewardedAdMetricsRow = RewardedAdMetricsResponse['rows'][number];
